import { FC, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InputMask from "react-input-mask";
import Swal from "sweetalert2";

import "./index.css"; /* Ajuste o caminho se necessário */
import api from "../../../../services/api";
import { Loading } from "../../../../components/Loading";
import useAuth from "../../../../hooks/useAuth";
import { usePluginspace } from "../../../../hooks/usePluginspace";
import { cepNoMask } from "../../../../functions/mask";
import { useAppConfig } from "../../../../hooks/useAppConfig"; // <-- Adicionado

const UpdateAddress: FC = () => {
  const [cep, setCep] = useState("");
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [number, setNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [ibge, setIbge] = useState("");

  const { loginInfo } = useAuth();
  const { getToken } = usePluginspace();
  const navigate = useNavigate();
  const { pluginspace_id, id_address } = useParams();

  // Estados para a Cor Primária
  const { getAppConfig } = useAppConfig();
  const [primaryColor, setPrimaryColor] = useState("#078BD8");

  const loadDataAndConfig = async () => {
    setLoading(true);
    try {
      // 1. Busca a cor primária
      const conf = await getAppConfig();
      if (conf?.webappcolor?.webappbutton) {
        setPrimaryColor(conf.webappcolor.webappbutton);
      }

      // 2. Busca o endereço para edição
      await api
        .post(
          `/api/customer/filter`,
          { cpf: loginInfo.identifier },
          { headers: { Authorization: `Bearer ${await getToken()}` } },
        )
        .then((response: any) => {
          if (response.data.data.length > 0) {
            const data = response.data.data[0];
            const [addr] = data.customerAddresses.filter(
              (add: any) => add.id == id_address,
            );
            if (addr) {
              setState(addr.state);
              setCity(addr.city);
              setCep(addr.zipCode);
              setDistrict(addr.district);
              setAddress(addr.street);
              setNumber(addr.number);
              setComplement(addr.extra);
            }
          }
        })
        .catch((err: any) => {
          console.log(err);
        });
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDataAndConfig();
  }, [loginInfo.identifier, id_address]);

  async function findCep(cepCode: string) {
    if (cepCode) {
      setLoading(true);
      return await api
        .get(`https://viacep.com.br/ws/${cepCode.replace("-", "")}/json/`)
        .then((response) => {
          if (response.data.erro) {
            setCity("");
            setAddress("");
            setDistrict("");
            setState("");
            Swal.fire({
              icon: "warning",
              title: "CEP inválido!",
              text: "Verifique se o CEP foi digitado corretamente.",
              confirmButtonColor: primaryColor,
            });
            setLoading(false);
            return false;
          } else {
            if (response.data.ibge !== "") setIbge(response.data.ibge);
            if (response.data.localidade !== "")
              setCity(response.data.localidade);
            if (response.data.logradouro !== "")
              setAddress(response.data.logradouro);
            if (response.data.bairro !== "") setDistrict(response.data.bairro);
            if (response.data.uf !== "") setState(response.data.uf);

            setLoading(false);
            return true;
          }
        })
        .catch(() => {
          setLoading(false);
          setCity("");
          setAddress("");
          setDistrict("");
          setState("");
          Swal.fire({
            icon: "warning",
            title: "CEP inválido!",
            text: "Verifique se o CEP foi digitado corretamente.",
            confirmButtonColor: primaryColor,
          });
          return false;
        });
    } else {
      return false;
    }
  }

  async function saveAddress() {
    if (cep && address && district && city && state && number) {
      setLoading(true);

      const updatedAddress = {
        zipCode: cep ? cepNoMask(cep) : "",
        addressId: parseInt(id_address!),
        customerId: loginInfo.id,
        street: address,
        number: number,
        district: district,
        city: city,
        state: state,
        extra: complement,
        reference: "",
        typeAddress: "main",
        ibge,
      };

      await api
        .put(`/api/customer/address`, updatedAddress, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((response) => {
          setLoading(false);
          if (response.data.success === true) {
            navigate(`/${pluginspace_id}/profile/addresses`);
          } else {
            Swal.fire({
              icon: "error",
              title: "Erro!",
              text: "Falha ao salvar endereço",
              confirmButtonColor: primaryColor,
            });
          }
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
          Swal.fire({
            icon: "error",
            title: "Erro!",
            text: "Falha ao salvar endereço",
            confirmButtonColor: primaryColor,
          });
        });
    } else {
      Swal.fire({
        icon: "warning",
        title: "Atenção",
        text: "Verifique se preencheu todos os campos obrigatórios.",
        confirmButtonColor: primaryColor,
      });
    }
  }

  return (
    <>
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            backgroundColor: "#FFF",
            zIndex: 10,
          }}
        >
          <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
        </div>
      ) : (
        <div className="perfil-address-form-content">
          <div className="perfil-address-form-header">
            <h4>Editar endereço</h4>
          </div>

          <div className="perfil-address-form-body">
            {/* CEP + Botão Localização */}
            <div className="perfil-address-input-group row-cep">
              <div>
                <label>CEP</label>
                <InputMask
                  className="perfil-address-input"
                  mask="99999-999"
                  type="text"
                  placeholder="00000-000"
                  value={cep}
                  onChange={(event) => setCep(event.target.value)}
                  onBlur={(event) => findCep(event.target.value)}
                />
              </div>
            </div>

            {/* Logradouro */}
            <div className="perfil-address-input-group">
              <label>Logradouro</label>
              <input
                className="perfil-address-input"
                type="text"
                placeholder="Nome da rua, avenida..."
                value={address}
                onChange={(event) => setAddress(event.target.value)}
              />
            </div>

            {/* Bairro */}
            <div className="perfil-address-input-group">
              <label>Bairro</label>
              <input
                className="perfil-address-input"
                type="text"
                placeholder="Nome do bairro"
                value={district}
                onChange={(event) => setDistrict(event.target.value)}
              />
            </div>

            {/* Número e Complemento na mesma linha */}
            <div className="perfil-address-input-group row">
              <div>
                <label>Número</label>
                <input
                  className="perfil-address-input"
                  type="text"
                  placeholder="123"
                  value={number}
                  onChange={(event) => setNumber(event.target.value)}
                />
              </div>
              <div>
                <label>Complemento</label>
                <input
                  className="perfil-address-input"
                  type="text"
                  placeholder="Apto, Sala..."
                  value={complement}
                  onChange={(event) => setComplement(event.target.value)}
                />
              </div>
            </div>

            {/* Cidade e Estado na mesma linha */}
            <div className="perfil-address-input-group row">
              <div>
                <label>Cidade</label>
                <input
                  className="perfil-address-input"
                  type="text"
                  placeholder="Cidade"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                />
              </div>
              <div>
                <label>Estado</label>
                <input
                  className="perfil-address-input"
                  type="text"
                  placeholder="UF"
                  value={state}
                  onChange={(event) => setState(event.target.value)}
                />
              </div>
            </div>
          </div>

          <button
            className="perfil-address-btn-save"
            style={{ background: primaryColor }}
            onClick={() => saveAddress()}
          >
            Salvar endereço
          </button>
        </div>
      )}
    </>
  );
};

export { UpdateAddress };
